/**
 * Scroll-state syncs batched into one read phase, before the pass writes.
 *
 * Binding a scroll view's under-bar and bottom-edge state (largeTitle.ts,
 * scrollEdge.ts) reads its scroll metrics and writes flags from them. Done
 * in the middle of a layout pass, each read follows DOM writes and forces
 * the browser to lay out again (six times on a split-view toggle with three
 * lists). While a batch is open the syncs are queued instead, and `flush`
 * measures all of them before applying any.
 *
 * The one metric the pass cannot know, the scroll offset, is read for every
 * scroll view when the batch opens, before any frame is written: a frame
 * does not move the offset, so the read costs the layout of what the ops
 * changed rather than of every frame the pass applied. (A pass that makes
 * the content shorter than the offset has the browser clamp it afterwards,
 * which fires `scroll`, and the sync on it catches up.) The extents the
 * frames change are the pass's own numbers (`ScrollExtent`).
 */

export interface ScrollSync<T> {
  measure(): T;
  apply(value: T): void;
}

/** A scroll view's content and viewport lengths on its axis, as the pass sets them. */
export interface ScrollExtent {
  content: number;
  viewport: number;
}

let batch: ScrollSync<unknown>[] | null = null;
const scrollers = new Set<HTMLElement>();
let offsets: Map<HTMLElement, number> | null = null;

/** Reads this scroll view's offset before each pass (a view made in a pass starts at 0). */
export function trackScroller(scroller: HTMLElement): void {
  scrollers.add(scroller);
  if (offsets && !offsets.has(scroller)) offsets.set(scroller, 0);
}

/**
 * Reads every tracked scroll view's offset: the renderer calls this before a
 * pass's first op touches the page, where nothing is waiting to be laid out.
 */
export function readScrollOffsets(): void {
  offsets = new Map();
  for (const scroller of scrollers) {
    if (!scroller.isConnected) {
      scrollers.delete(scroller);
      continue;
    }
    offsets.set(scroller, scroller.scrollTop);
  }
}

/** Forgets the offsets read for a pass (a pass that did not lay out). */
export function discardScrollOffsets(): void {
  offsets = null;
}

/** The scroll offset read when the batch opened, else read now. */
export function scrollTopOf(scroller: HTMLElement): number {
  return offsets?.get(scroller) ?? scroller.scrollTop;
}

/** Run `sync` now, or queue it while a batch is open. */
export function runScrollSync<T>(sync: ScrollSync<T>): void {
  if (batch) batch.push(sync as ScrollSync<unknown>);
  else sync.apply(sync.measure());
}

/** Opens a batch, reading the offsets now unless the pass read them before its writes. */
export function beginScrollSyncBatch(): void {
  batch ??= [];
  if (!offsets) readScrollOffsets();
}

/** Measure every queued sync, then apply them all; closes the batch. */
export function flushScrollSyncBatch(): void {
  const queued = batch;
  batch = null;
  if (!queued) {
    discardScrollOffsets();
    return;
  }
  const values = queued.map((sync) => sync.measure());
  discardScrollOffsets();
  queued.forEach((sync, i) => sync.apply(values[i]));
}
